import { useCallback, useEffect, useState } from "react";
import { Loader2, MessageCircle, Search, Clock } from "lucide-react";
import api from "@/lib/api";
import { localeTag } from "@/i18n";
import { Input } from "@/components/ui/input";
import { WhatsAppConnect } from "@/components/whatsapp/WhatsAppConnect";
import { ChatThread } from "@/components/whatsapp/ChatThread";

const when = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date().toDateString() === d.toDateString()
    ? d.toLocaleTimeString(localeTag(), { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString(localeTag(), { day: "2-digit", month: "short" });
};

const ConvItem = ({ c, active, onOpen }) => (
  <button type="button" onClick={() => onOpen(c.wa_id)} data-testid={`wa-conv-${c.wa_id}`}
    className={`w-full text-left px-4 py-3 flex gap-3 border-l-2 transition-colors hover:bg-secondary/60 ${active ? "bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-500" : "border-transparent"}`}>
    <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 flex items-center justify-center font-bold shrink-0" data-no-translate>
      {(c.contact || c.name || "+").slice(0, 1).toUpperCase()}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className={`truncate text-sm ${c.unread ? "font-bold" : "font-medium"}`} data-no-translate>{c.contact || c.name || `+${c.wa_id}`}</span>
        <span className="ml-auto text-[11px] text-muted-foreground shrink-0">{when(c.last?.at)}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground truncate flex-1" data-no-translate>{c.last?.direction === "out" ? "Tu: " : ""}{c.last?.body || (c.last?.type !== "text" ? "Allegato" : "")}</span>
        {c.window_open && <Clock className="w-3 h-3 text-emerald-600 shrink-0" title="Puoi rispondere" />}
        {c.unread > 0 && <span className="text-[10px] font-bold rounded-full bg-emerald-600 text-white px-1.5 min-w-[18px] text-center">{c.unread}</span>}
      </div>
    </div>
  </button>
);

export default function WhatsApp() {
  const [cfg, setCfg] = useState(undefined);
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/whatsapp/config").then((r) => setCfg(r.data.account)).catch(() => setCfg({ connected: false })); }, []);
  const load = useCallback(() => api.get("/whatsapp/conversations").then((r) => setConvs(r.data)).finally(() => setLoading(false)), []);
  useEffect(() => {
    if (!cfg?.connected) return undefined;
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, [cfg?.connected, load]);

  if (cfg === undefined) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (!cfg.connected) return <div className="max-w-3xl" data-testid="wa-setup"><WhatsAppConnect onChange={setCfg} /></div>;

  const shown = convs.filter((c) => !q || `${c.contact} ${c.name} ${c.wa_id}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden grid lg:grid-cols-[360px_1fr] h-[calc(100vh-9.5rem)] min-h-[520px]" data-testid="wa-page">
      <div className={`flex flex-col min-h-0 border-r border-border ${sel ? "hidden lg:flex" : "flex"}`}>
        <div className="p-3 border-b border-border space-y-2">
          <div className="text-xs text-muted-foreground px-1" data-no-translate>{cfg.display_phone} {cfg.verified_name ? `· ${cfg.verified_name}` : ""}</div>
          <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca chat…" className="pl-9 h-9" data-testid="wa-search-input" /></div>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-border" data-testid="wa-conversations">
          {loading && <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>}
          {!loading && shown.map((c) => <ConvItem key={c.wa_id} c={c} active={sel === c.wa_id} onOpen={setSel} />)}
          {!loading && !shown.length && <div className="p-10 text-center text-sm text-muted-foreground"><MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />Nessuna chat. I messaggi dei clienti compariranno qui.</div>}
        </div>
      </div>
      <div className={`min-h-0 flex-col ${sel ? "flex" : "hidden lg:flex"}`}>
        {sel ? <ChatThread waId={sel} onBack={() => setSel(null)} onSent={load} />
          : <div className="flex-1 flex flex-col items-center justify-center text-sm text-muted-foreground gap-2"><MessageCircle className="w-8 h-8 opacity-40" />Seleziona una chat</div>}
      </div>
    </div>
  );
}
